import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Plus, TrendingUp, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GoalsPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [goals, setGoals] = useState([]);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    goal_type: 'milestone',
    target_date: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/goals`, {
        headers: getAuthHeader()
      });
      setGoals(response.data || []);
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Please enter a goal title');
      return;
    }

    try {
      await axios.post(`${API}/goals`, formData, {
        headers: getAuthHeader()
      });
      
      toast.success('Goal created!');
      setShowNewGoal(false);
      setFormData({ title: '', description: '', goal_type: 'milestone', target_date: '' });
      fetchGoals();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const markComplete = async (goalId) => {
    try {
      await axios.put(`${API}/goals/${goalId}/complete`, {}, {
        headers: getAuthHeader()
      });
      
      toast.success('Goal achieved! +100 points! 🎉');
      fetchGoals();
      
      // Reload to show updated points
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error('Failed to mark as complete');
    }
  };

  const updateProgress = async (goalId, newProgress) => {
    try {
      await axios.put(`${API}/goals/${goalId}/progress`, 
        { progress_percentage: newProgress },
        { headers: getAuthHeader() }
      );
      
      toast.success('Progress updated!');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const goalTemplates = [
    { title: 'Score First 50', type: 'milestone', description: 'Achieve 50 runs in a single innings' },
    { title: 'Score First Century', type: 'milestone', description: 'Achieve 100 runs in a single innings' },
    { title: 'Take 5 Wickets', type: 'milestone', description: 'Take 5 wickets in a single match' },
    { title: 'Improve Strike Rate', type: 'skill', description: 'Increase strike rate to 140+' },
    { title: 'Master Cover Drive', type: 'skill', description: 'Perfect cover drive technique' },
    { title: 'Get Selected for MiLC', type: 'selection', description: 'Get selected for Minor League Cricket team' }
  ];

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/player-dashboard')} className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>My Cricket Goals</h1>
            <Button onClick={() => setShowNewGoal(!showNewGoal)} className="bg-gradient-to-r from-purple-500 to-pink-500">
              <Plus className="w-4 h-4 mr-2" />
              {showNewGoal ? 'Cancel' : 'New Goal'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {showNewGoal && (
          <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Create New Goal</h2>
            
            <div className="mb-6">
              <h3 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Quick Templates:</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {goalTemplates.map((template, idx) => (
                  <button
                    key={`${template.type}-${template.title}-${idx}`}
                    onClick={() => setFormData({...formData, title: template.title, description: template.description, goal_type: template.type})}
                    className={`p-3 rounded-lg text-left ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                  >
                    <div className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{template.title}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>{template.type}</div>
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={createGoal} className="space-y-4">
              <Input
                placeholder="Goal title (e.g., Score my first 50)"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
              />
              
              <Textarea
                placeholder="Description (optional)"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <Select value={formData.goal_type} onValueChange={(v) => setFormData({...formData, goal_type: v})}>
                  <SelectTrigger className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="milestone">Milestone</SelectItem>
                    <SelectItem value="skill">Skill</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="selection">Selection</SelectItem>
                  </SelectContent>
                </Select>
                
                <Input
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => setFormData({...formData, target_date: e.target.value})}
                  className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                />
              </div>
              
              <Button type="submit" className="w-full bg-gradient-to-r from-green-500 to-emerald-600">
                Create Goal
              </Button>
            </form>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner"></div>
          </div>
        ) : goals.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {goals.map((goal) => (
              <Card key={goal.id} className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{goal.title}</h3>
                      <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{goal.description}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                      {goal.goal_type}
                    </span>
                  </div>
                  
                  {goal.target_date && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Clock className="w-4 h-4 text-orange-400" />
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        Target: {new Date(goal.target_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  <div>
                    <div className="flex items-center justify-between mb-2 text-sm">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Progress</span>
                      <span className={`font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                        {goal.progress_percentage || 0}%
                      </span>
                    </div>
                    <Progress value={goal.progress_percentage || 0} className="h-2" />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {goal.status === 'achieved' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <TrendingUp className="w-5 h-5 text-blue-400" />
                    )}
                    <span className={`text-sm font-medium ${goal.status === 'achieved' ? 'text-green-500' : (theme === 'dark' ? 'text-blue-400' : 'text-blue-600')}`}>
                      {goal.status === 'achieved' ? 'Achieved!' : 'In Progress'}
                    </span>
                  </div>
                  
                  {goal.status !== 'achieved' && (
                    <div className="space-y-3 mt-4">
                      <div className="flex items-center space-x-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          defaultValue={goal.progress_percentage || 0}
                          placeholder="Progress %"
                          id={`progress-${goal.id}`}
                          className={`flex-1 ${theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const input = document.getElementById(`progress-${goal.id}`);
                            updateProgress(goal.id, parseInt(input.value) || 0);
                          }}
                        >
                          Update
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => markComplete(goal.id)}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark as Achieved
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Target className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-xl mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No goals set yet</p>
            <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Set your first cricket goal to start tracking progress!</p>
            <Button onClick={() => setShowNewGoal(true)} className="bg-gradient-to-r from-purple-500 to-pink-500">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Goal
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
